import React, { useRef, useState } from 'react';

import analytics from '@repo/analytics';
import { subscriptionApi, SubscriptionPlanType } from '@repo/subscription/api';
import { fireToast } from '@repo/ui/components/Toast';
import { buildEditorUrl, FEATURE_REQUEST_PLATFORM_URL } from '@repo/utils/url';

import {
  OFFBOARDING_REASON_SELECTION,
  SUBSCRIPTION_CANCEL,
} from '../../../global/analytics/events';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { formatMonetaryValue } from '../../../utils/billing';
import HandshakeIcon from '../../utilities/icons/HandshakeIcon';
import InspirationIcon from '../../utilities/icons/InspirationIcon';
import usePlanPrice from '../hooks/usePlanPrice';
import { getDateLabel, isYearlySubscription } from '../utils';

import AreYouSureContent from './AreYouSureContent/AreYouSureContent';
import { OffboardingReasonId, offboardingReasons } from './config';
import CongratulationsContent from './CongratulationsContent';
import DiscountOfferContent from './DiscountOfferContent/DiscountOfferContent';
import QuestionnaireContent from './QuestionnaireContent/QuestionnaireContent';
import { QuestionnaireContentProps } from './QuestionnaireContent/types';
import RoadmapContent from './RoadmapContent/RoadmapContent';
import { OffboardingContentWrapper } from './styles';
import SwitchToMonthlyContent from './SwitchToMonthlyContent/SwitchToMonthlyContent';
import { GeneralOffboardingSteps, OffboardingModalContentProps } from './types';

const MONTHLY_DISCOUNT_PERCENT_OFF = 25;

const GeneralOffboardingModalContent: React.FC<
  OffboardingModalContentProps
> = ({
  user,
  subscription,
  backButtonLabel,
  onClose,
  onSuccess,
  onStartLoading,
  onFinishLoading,
  skipToCancel,
}) => {
  const [offboardingStep, setOffboardingStep] =
    useState<GeneralOffboardingSteps>(GeneralOffboardingSteps.Questionnaire);
  const [reason, setReason] = useState<OffboardingReasonId | null>(null);

  const extraInputRef = useRef<string | null>(null);
  const { setYearly } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const plan = subscription.pendingPlan?.name ?? subscription?.plan?.name;
  const monthlyPrice = usePlanPrice({
    plan: plan ?? SubscriptionPlanType.PRO,
    isYearly: false,
  });

  if (!subscription?.plan) {
    return null;
  }

  const handleQuestionnaireNextClick: QuestionnaireContentProps['primaryButton']['onClick'] =
    ({ reasonId, extraInput }): void => {
      setReason(reasonId);

      void analytics.track(OFFBOARDING_REASON_SELECTION, {
        reasonId,
        extraInput,
      });

      extraInputRef.current = extraInput;

      const isYearly = isYearlySubscription(subscription);

      if (skipToCancel) {
        setOffboardingStep(GeneralOffboardingSteps.AreYouSure);
        return;
      }

      if (isYearly && reasonId === 'doesNotFitNeeds') {
        setOffboardingStep(GeneralOffboardingSteps.Roadmap);
      } else if (isYearly) {
        setOffboardingStep(GeneralOffboardingSteps.SwitchToMonthly);
      } else if (
        !!subscription.canApplyCancellationDiscount &&
        subscription.nextInvoiceDate
      ) {
        setOffboardingStep(GeneralOffboardingSteps.DiscountOffer);
      } else {
        setOffboardingStep(GeneralOffboardingSteps.AreYouSure);
      }
    };

  const handlePayMonthlyClick = (): void => {
    onClose();
    setYearly(false);
  };

  const handleSubscriptionCancelClick = async (): Promise<void> => {
    const cancelReason = `${reason}${
      extraInputRef.current ? ` (${extraInputRef.current})` : ''
    }`;

    onStartLoading();
    const response = await subscriptionApi.cancelSubscription({
      reason: cancelReason,
    });
    fireToast({
      label: response.success
        ? 'Your subscription was canceled'
        : `Failed to cancel your subscription. Please contact our support. ${response.error}`,
      error: !response.success,
      duration: 5000,
    });

    onFinishLoading();
    if (response.success) {
      if (user) {
        void analytics.track(SUBSCRIPTION_CANCEL, { userId: user.id });
      }
      onSuccess?.(response.subscription);
      onClose();
    }
  };

  if (offboardingStep === GeneralOffboardingSteps.Questionnaire) {
    return (
      <OffboardingContentWrapper>
        <QuestionnaireContent
          header={
            <>
              {user.name},
              <br />
              we are sorry to see you go!
            </>
          }
          primaryButton={{
            label: 'Next',
            onClick: handleQuestionnaireNextClick,
          }}
          reasons={offboardingReasons}
          secondaryButton={{
            label: backButtonLabel ?? 'Back to billing',
            onClick: onClose,
          }}
        />
      </OffboardingContentWrapper>
    );
  }

  if (offboardingStep === GeneralOffboardingSteps.Congrats) {
    return (
      <OffboardingContentWrapper>
        <CongratulationsContent user={user} />
      </OffboardingContentWrapper>
    );
  }

  const handleDiscountAcceptClick = async (): Promise<void> => {
    onStartLoading();

    const response = await subscriptionApi.applyCancellationDiscount();

    if (!response.success) {
      console.error(`Failed to apply cancellation discount: ${response.error}`);

      fireToast({
        label: 'We are sorry, something went wrong',
        error: true,
        duration: 5000,
      });
    } else {
      onSuccess?.(response.subscription);
      setOffboardingStep(GeneralOffboardingSteps.Congrats);
    }

    onFinishLoading();
  };

  if (offboardingStep === GeneralOffboardingSteps.Roadmap) {
    return (
      <OffboardingContentWrapper>
        <RoadmapContent
          heading="Suggest your ideas"
          icon={<InspirationIcon style={{ alignSelf: 'center' }} />}
          primaryButton={{
            label: 'Request a Feature',
            onClick: (): void => {
              window.open(FEATURE_REQUEST_PLATFORM_URL, '_blank');
            },
          }}
          secondaryButton={{
            label: 'Cancel Subscription',
            onClick: handleSubscriptionCancelClick,
          }}
        />
      </OffboardingContentWrapper>
    );
  }

  if (offboardingStep === GeneralOffboardingSteps.SwitchToMonthly) {
    return (
      <OffboardingContentWrapper>
        <SwitchToMonthlyContent
          monthlyPrice={monthlyPrice}
          onPayMonthly={handlePayMonthlyClick}
          onSubscriptionCancel={handleSubscriptionCancelClick}
        />
      </OffboardingContentWrapper>
    );
  }

  if (offboardingStep === GeneralOffboardingSteps.DiscountOffer) {
    const discountInvoicePrice = {
      ...monthlyPrice,
      amount: monthlyPrice.amount * (1 - MONTHLY_DISCOUNT_PERCENT_OFF / 100),
    };

    const dateLabel = getDateLabel(subscription.nextInvoiceDate);

    const billedText = `Billed ${formatMonetaryValue(discountInvoicePrice, {
      fractionDigits: 0,
    })} on ${dateLabel}`;

    const heading =
      reason === 'pause'
        ? `Get ${MONTHLY_DISCOUNT_PERCENT_OFF}% discount`
        : 'Give Kittl another chance';

    const discountOptionTitle = `${MONTHLY_DISCOUNT_PERCENT_OFF}% off for your next month`;
    const discountOptionSubtitle =
      reason === 'pause'
        ? billedText
        : `Explore what Kittl has to offer! ${billedText}`;

    return (
      <OffboardingContentWrapper>
        <DiscountOfferContent
          discountOptionSubtitle={discountOptionSubtitle}
          discountOptionTitle={discountOptionTitle}
          heading={heading}
          icon={HandshakeIcon}
          onBackClick={onClose}
          onDiscountAccept={handleDiscountAcceptClick}
          onSubscriptionCancel={handleSubscriptionCancelClick}
        />
      </OffboardingContentWrapper>
    );
  }

  return (
    <OffboardingContentWrapper narrow>
      <AreYouSureContent
        primaryButton={{
          label: 'Explore Kittl',
          onClick: (): void => {
            window.open(buildEditorUrl());
          },
        }}
        secondaryButton={{
          label: 'Cancel subscription',
          onClick: handleSubscriptionCancelClick,
        }}
      />
    </OffboardingContentWrapper>
  );
};

export default GeneralOffboardingModalContent;
